/**
 * AppNotificationsPanel — Surface Layer (/01).
 *
 * A header-anchored notifications panel modeled on the IBM Products
 * `NotificationsPanel` pattern (Carbon for IBM Products), composed locally on
 * top of the `@carbon/react` UIShell `HeaderPanel` so it slides out of the app
 * header. It owns its own filter state and reads live data through the existing
 * `useNotifications` persistence hook (mirroring `UserHubDashboard` usage).
 *
 * Action surface (tenant-role gated, presentation-only; the SECURITY DEFINER
 * `notification_act` RPC re-enforces authority server-side):
 *   - Approve / Reject / Run     for elevated users on actionable items
 *   - Delegate up                when the caller's role is below the required rank
 *   - Archive / Unarchive        in the OverflowMenu on every row
 *   - Mark read                  in the OverflowMenu on unread rows
 *   - View inbox                 link to the full /notifications route
 *
 * All rendered content is client-safe: only title, body, kind, read state and
 * created timestamp are surfaced — never trace data, secrets or internal paths.
 */

import { useState, useCallback } from 'react';
import {
  Button,
  HeaderPanel,
  InlineNotification,
  OverflowMenu,
  OverflowMenuItem,
  SkeletonText,
} from '@carbon/react';
import {
  ArrowRight,
  CheckmarkOutline,
  Close,
  Play,
  Events,
} from '@carbon/icons-react';
import { useNotifications, useMe } from '@lib/persistence/useRuntimeData';
import type { NotificationRow as NotificationData } from '@contracts/runtime-endpoints.contract';
import { NotificationFilters, type NotificationFilterValue } from './NotificationFilters';
import { NotificationRow } from './NotificationRow';
import { NotificationEmptyState } from './NotificationEmptyState';
import {
  roleRank,
  isElevatedRole,
  requiredRankFor,
  needsDelegate,
  primaryActionKind,
} from './notification-actions';
import './notifications-panel.scss';

export interface AppNotificationsPanelProps {
  /** Whether the panel is expanded (slid out from the header). */
  open: boolean;
  /** Called when the panel requests to close (focus/blur collapse). */
  onClose: () => void;
}

const PANEL_LIMIT = 25;

/** Format an ISO timestamp into a short, locale-aware client-safe label. */
function formatTimestamp(iso?: string): string | undefined {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toLocaleString();
}

type Feedback = { kind: 'success' | 'info' | 'error'; text: string } | null;

export function AppNotificationsPanel({ open, onClose }: AppNotificationsPanelProps) {
  const [filter, setFilter] = useState<NotificationFilterValue>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const { notifications, unreadCount, isLoading, ack, act, delegate, archive } = useNotifications(
    filter === 'unread' ? 'unread' : undefined,
    PANEL_LIMIT,
  );
  const { me } = useMe();

  const role = me?.role ?? 'user';
  const callerRank = roleRank(role);
  const isElevated = isElevatedRole(role);

  const runAction = useCallback(
    async (n: NotificationData, decision: 'approve' | 'reject' | 'execute' | 'delegate') => {
      setBusy(n.id);
      setFeedback(null);
      const r = decision === 'delegate' ? await delegate(n.id) : await act(n.id, decision);
      setBusy(null);
      if (!r.ok) {
        setFeedback({ kind: 'error', text: 'Action not permitted or failed.' });
        return;
      }
      if (r.status === 'delegated') {
        setFeedback({
          kind: 'info',
          text: `Escalated to ${r.delegatedToRole ?? 'a higher role'} — someone with that authority will decide.`,
        });
        return;
      }
      const globalNote = r.global ? ' Tenant-wide decision.' : '';
      setFeedback({
        kind: 'success',
        text: (r.dispatchedIntent
          ? `Dispatched ${r.dispatchedIntent} — the worker will process it.`
          : `Recorded: ${decision}.`) + globalNote,
      });
    },
    [act, delegate],
  );

  const onAck = useCallback(
    async (n: NotificationData) => {
      setBusy(n.id);
      setFeedback(null);
      const ok = await ack(n.id);
      setBusy(null);
      if (!ok) setFeedback({ kind: 'error', text: 'Could not mark as read.' });
    },
    [ack],
  );

  const onArchive = useCallback(
    async (n: NotificationData, archived: boolean) => {
      setBusy(n.id);
      setFeedback(null);
      const ok = await archive(n.id, archived);
      setBusy(null);
      if (!ok) setFeedback({ kind: 'error', text: `Could not ${archived ? 'archive' : 'unarchive'}.` });
    },
    [archive],
  );

  function renderActions(n: NotificationData) {
    const overflow = (
      <OverflowMenu size="sm" aria-label="Notification actions" flipped>
        <OverflowMenuItem
          itemText="Archive"
          disabled={busy === n.id}
          onClick={() => void onArchive(n, true)}
        />
        {!n.read_at ? (
          <OverflowMenuItem
            itemText="Mark read"
            disabled={busy === n.id}
            onClick={() => void onAck(n)}
          />
        ) : null}
      </OverflowMenu>
    );

    // Already-acted notifications show their final state and the overflow only.
    if (n.status && n.status !== 'open') {
      return (
        <span className="notification-item__action-group">
          <span className="notification-item__status">{n.acted_result ?? n.status}</span>
          {overflow}
        </span>
      );
    }

    const kind = primaryActionKind(n);
    const mustEscalate = needsDelegate(n, callerRank, isElevated);

    if (kind === 'approve' || kind === 'decision' || kind === 'execute') {
      if (mustEscalate) {
        return (
          <span className="notification-item__action-group">
            <Button
              size="sm"
              kind="ghost"
              renderIcon={Events}
              disabled={busy === n.id}
              onClick={() => void runAction(n, 'delegate')}
            >
              Delegate up
            </Button>
            {overflow}
          </span>
        );
      }
      if (kind === 'execute') {
        return (
          <span className="notification-item__action-group">
            <Button
              size="sm"
              kind="primary"
              renderIcon={Play}
              disabled={busy === n.id}
              onClick={() => void runAction(n, 'execute')}
            >
              Run
            </Button>
            {overflow}
          </span>
        );
      }
      return (
        <span className="notification-item__action-group">
          <Button
            size="sm"
            kind="primary"
            renderIcon={CheckmarkOutline}
            disabled={busy === n.id}
            onClick={() => void runAction(n, 'approve')}
          >
            Approve
          </Button>
          <Button
            size="sm"
            kind="danger--tertiary"
            renderIcon={Close}
            disabled={busy === n.id}
            onClick={() => void runAction(n, 'reject')}
          >
            Reject
          </Button>
          {overflow}
        </span>
      );
    }

    return overflow;
  }

  const isEmpty = !isLoading && notifications.length === 0;

  return (
    <HeaderPanel
      aria-label="Notifications"
      expanded={open}
      onHeaderPanelFocus={onClose}
      className="app-notifications-panel"
    >
      <div className="app-notifications-panel__header">
        <h2 className="app-notifications-panel__title">Notifications</h2>
        <span className="app-notifications-panel__role" aria-label="Active role">
          role: {role}
        </span>
      </div>

      <NotificationFilters value={filter} onChange={setFilter} unreadCount={unreadCount} />

      {feedback ? (
        <div className="app-notifications-panel__feedback">
          <InlineNotification
            kind={feedback.kind}
            lowContrast
            title={
              feedback.kind === 'success' ? 'Done' : feedback.kind === 'error' ? 'Blocked' : 'Info'
            }
            subtitle={feedback.text}
            onClose={() => setFeedback(null)}
          />
        </div>
      ) : null}

      {isLoading ? (
        <div className="app-notifications-panel__loading">
          <SkeletonText paragraph lineCount={3} />
        </div>
      ) : isEmpty ? (
        <div className="app-notifications-panel__list">
          <NotificationEmptyState filter={filter} />
        </div>
      ) : (
        <div className="app-notifications-panel__list">
          {notifications.map((n) => (
            <NotificationRow
              key={n.id}
              title={n.title}
              body={n.body}
              kind={n.kind}
              unread={!n.read_at}
              timestamp={formatTimestamp(n.created_at)}
              actions={renderActions(n)}
            />
          ))}
        </div>
      )}

      <div className="app-notifications-panel__footer">
        <Button kind="ghost" size="sm" renderIcon={ArrowRight} href="/notifications">
          Open inbox
        </Button>
      </div>
    </HeaderPanel>
  );
}
